import { Component, EventEmitter, HostListener, Inject, OnInit, Output, ViewEncapsulation } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { Router } from "@angular/router";

import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";

import { Observable } from "rxjs";
import { select, Store } from "@ngrx/store";
import { AppState, sitesStateIsLoading } from "src/app/store/reducers";
import { createDispenser, updateDispenser } from "src/app/store/actions/sites/sites.actions";

import { TranslateService } from "@ngx-translate/core";
import { SnackbarService } from "src/app/shared/services/snackbar/snackbar.service";

import { SiteDispenserDTO } from "src/app/models/Sites/siteDispenserDTO";
import { SiteFuelPointDTO } from "src/app/models/Sites/siteFuelPointDTO";
import { Dialog, GuardConfirmationDialogData } from "src/app/models/dialog/dialog.model";
import { SnackbarType } from "src/app/models/snackbar/snackbar.model";
import { isAuthenticated } from "src/app/shared/utils/authenticationUtils";
import { FormUtils } from "src/app/shared/utils/formUtils";

import { CanComponentDeactivate } from "src/app/shared";
import { ConfirmationDialogComponent } from "src/app/shared/modules/dialogs/confirmation-dialog/confirmation-dialog.component";

//Dispenser Detail Form
const DISPENSER_FORM_PARAMS = {
  enabled: "enabled",
  dispenserNumber: "dispenserNumber",
  serialNumber: "serialNumber",
  description: "description",
  fuelPoints: "fuelPoints",
};


@Component({
  selector: "app-dispenser-form-dialog",
  templateUrl: "./dispenser-form-dialog.component.html",
  styleUrls: ["./dispenser-form-dialog.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class DispenserFormDialogComponent implements OnInit, CanComponentDeactivate {
  @Output() getDispenserForm: EventEmitter<FormGroup> = new EventEmitter<FormGroup>();

  isLoading$: Observable<boolean>;

  siteCode: string;
  currentDispenser: SiteDispenserDTO;

  fuelPointList: SiteFuelPointDTO[];

  dispenserDataForm: FormGroup;

  isCreate: boolean = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<DispenserFormDialogComponent>,
    private fb: FormBuilder,
    private router: Router,
    private store: Store<AppState>,
    private snackbarService: SnackbarService,
    private translate: TranslateService
  ) {
    this.isLoading$ = this.store.pipe(select(sitesStateIsLoading));
  }

  ngOnInit(): void {
    this.setDialogData();
    this.createDispenserForm();
    this.setFormValues(this.currentDispenser);
  }

  setDialogData() {
    this.isCreate = this.data.isCreate;
    this.siteCode = this.data.siteCode;
    this.currentDispenser = this.data.currentDispenser;
    this.fuelPointList = this.data.fuelPointList;
  }

  createDispenserForm() {
    this.dispenserDataForm = this.fb.group({
      [DISPENSER_FORM_PARAMS.enabled]: [true],
      [DISPENSER_FORM_PARAMS.dispenserNumber]: [
        null,
        [Validators.required, Validators.min(1), Validators.max(255)],
      ],
      [DISPENSER_FORM_PARAMS.serialNumber]: [null, [Validators.maxLength(50)]],
      [DISPENSER_FORM_PARAMS.description]: [
        null,
        [Validators.required, FormUtils.noWhitespaceValidator, Validators.maxLength(45)],
      ],
      [DISPENSER_FORM_PARAMS.fuelPoints]: [null],
    });
    this.subscribeFormChanges(this.dispenserDataForm);
  }

  subscribeFormChanges(form: FormGroup) {
    form.valueChanges.subscribe(() => {
      this.getDispenserPristineValue();
    });
  }

  setFormValues(dispenser: SiteDispenserDTO, duplication?: boolean) {
    this.dispenserDataForm.controls.enabled.setValue(
      dispenser?.enabled ?? true
    );
    this.dispenserDataForm.controls.dispenserNumber.setValue(
      duplication ? null : dispenser?.dispenserNumber ?? null
    );
    this.dispenserDataForm.controls.serialNumber.setValue(
      duplication ? null : dispenser?.serialNumber ?? null
    );
    this.dispenserDataForm.controls.description.setValue(
      dispenser?.description ?? null
    );
    this.dispenserDataForm.controls.fuelPoints.setValue(
      dispenser?.fuelPoints
        ? dispenser?.fuelPoints.map(({ code }) => code)
        : null
    );
  }

  duplicate() {
    this.setFormValues(this.currentDispenser, true);

    this.isCreate = true;
    this.currentDispenser = null;
  }

  onSubmit() {
    if (!this.dispenserDataForm.valid) {
      this.dispenserDataForm.markAllAsTouched();
      this.snackbarService.show({
        message: this.translate.instant("SiteManagement.formInvalid"),
        type: SnackbarType.ERROR,
      });
      return;
    }

    if (this.dispenserDataForm.pristine && !this.isCreate) {
      this.snackbarService.show({
        message: this.translate.instant("SiteManagement.NoChanges"),
        type: SnackbarType.WARNING,
      });
      return;
    }

    let dispenser: SiteDispenserDTO = {
      code: this.currentDispenser?.code ?? null,
      siteCode: this.siteCode ?? null,
      enabled: this.dispenserDataForm.controls.enabled.value ?? true,
      dispenserNumber: this.dispenserDataForm.controls.dispenserNumber.value ?? null,
      serialNumber: this.dispenserDataForm.controls.serialNumber.value ?? null,
      description: this.dispenserDataForm.controls.description.value ?? null,
      fuelPointCodes: this.dispenserDataForm.controls.fuelPoints.value ?? null,
    };

    if (this.isCreate) {
      this.store.dispatch(createDispenser({ dispenser }));
    } else {
      this.store.dispatch(updateDispenser({ dispenser }));
    }

    this.dispenserDataForm.markAsUntouched();
    this.dispenserDataForm.markAsPristine();
    this.getDispenserPristineValue();
  }

  getDispenserPristineValue() {
    this.getDispenserForm.emit(this.dispenserDataForm);
  }

  close() {
    this.dialogRef.close();
  }

  @HostListener("window:beforeunload")
  canDeactivate(): Observable<boolean> | boolean {
    return this.isNavigationAllowed();
  }

  isNavigationAllowed(): Observable<boolean> | boolean {
    if (!isAuthenticated(this.router.url) || this.dispenserDataForm.pristine) {
      return true;
    } else {
      return false;
    }
  }
  confirmDialog(dialog: any): Observable<boolean> {
    const dialogData: Dialog = GuardConfirmationDialogData;
    const dialogRef = dialog.open(ConfirmationDialogComponent, dialogData);
    return dialogRef.afterClosed();
  }
}
